import { LoadingButton } from "@mui/lab";
import { Box, FormControl, Typography } from "@mui/material";
import { useFormik } from "formik";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PasswordInput from "../../components/PasswordInput";
import { changePassword, getAuthData } from "../../features/auth/authSlice";
import Alert from "../../utils/alert";
import config from "../../utils/config";
import { Roles } from "../../utils/helper";
import { userResetPassword } from "../../utils/validators";

const ChangeDefaultPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, user } = useSelector(getAuthData);

  const {
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
    handleFocus,
  } = useFormik({
    validationSchema: userResetPassword,
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    onSubmit: async (value) => {
      try {
        // Check if passwords match
        if (value.newPassword !== value.confirmPassword) {
          Alert({
            type: "error",
            message: "Passwords do not match",
          });
          return;
        }

        // Change password
        const res = await dispatch(
          changePassword({ newPassword: value.newPassword })
        ).unwrap();

        if (res?.success) {
          Alert({
            type: "success",
            message: "Password changed successfully! Please login with your new password.",
          });

          // Redirect to appropriate dashboard based on role
          const { companyStaff, ...roles } = Roles;
          if (user && Object.values(roles).includes(user?.role?.[0])) {
            let redirectUrl;
            if (user?.role?.[0] === Roles.hospitalStaff)
              redirectUrl = config.routes.hospitals.patients.patient;
            else redirectUrl = config.routes.dashboard;

            window.location.href = redirectUrl;
          } else {
            // Fallback to login
            window.location.href = config.routes.login;
          }
        }
      } catch (error) {
        console.log(error);
        Alert({
          type: "error",
          message: error?.message || "Failed to change password. Please try again.",
        });
      }
    },
  });

  return (
    <Box className="min-h-screen flex items-center justify-center bg-gray-50">
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit}
        className="w-full max-w-md"
        data-cy="change-default-password-page"
      >
        <Box className="px-8 py-10 bg-[var(--c-bg-color)] rounded-lg shadow-md">
          <Typography className="text-[24px] font-bold mb-2 text-[#040316]">
            Change Default Password
          </Typography>
          <Typography className="text-[14px] mb-6 text-gray-600">
            Welcome! For security reasons, please change your default password before continuing.
          </Typography>

          <FormControl className="w-full mb-4">
            <PasswordInput
              {...{
                value: values.newPassword,
                label: "New Password",
                error: errors.newPassword && touched.newPassword,
                helperText:
                  errors.newPassword && touched.newPassword
                    ? errors.newPassword
                    : "",
                name: "newPassword",
                onChange: handleChange,
                onBlur: handleBlur,
                onFocus: handleFocus,
                required: true,
                inputProps: {
                  "data-cy": "new-password-input",
                },
              }}
            />
          </FormControl>

          <FormControl className="w-full mb-6">
            <PasswordInput
              {...{
                value: values.confirmPassword,
                label: "Confirm New Password",
                error: errors.confirmPassword && touched.confirmPassword,
                helperText:
                  errors.confirmPassword && touched.confirmPassword
                    ? errors.confirmPassword
                    : "",
                name: "confirmPassword",
                onChange: handleChange,
                onBlur: handleBlur,
                onFocus: handleFocus,
                required: true,
                inputProps: {
                  "data-cy": "confirm-password-input",
                },
              }}
            />
          </FormControl>

          <LoadingButton
            fullWidth
            className="rounded-[10px] bg-[var(--c-primary-0)] hover:bg-[var(--c-primary-1)] normal-case"
            sx={{
              ".MuiLoadingButton-loadingIndicatorCenter": {
                color: "var(--c-bg-color) !important",
              },
            }}
            size="large"
            type="submit"
            loading={loading}
            data-cy="change-password-submit-button"
            variant="contained"
          >
            <span>Change Password</span>
          </LoadingButton>

          <Typography className="text-[12px] mt-4 text-center text-gray-500">
            Password must contain at least 1 uppercase, lowercase, number and special character
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ChangeDefaultPassword;
